'use client';

import { c as _c } from "react/compiler-runtime";
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { useRouter } from '../adapters/next';
import { CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut, Kbd, KbdGroup } from '@repo/ui/components';
import { ArrowLeft } from '@repo/ui/icons';
import type { CommandWorkflowContentProps, CommandWorkflowContext, CommandWorkflowNode, CommandWorkflowPage } from './types';
export function CommandWorkflowContent(t0) {
  const $ = _c(33);
  const {
    close,
    initialPageId,
    inputPlaceholder,
    inputTestId,
    workflow
  } = t0;
  const {
    push
  } = useRouter();
  const [search, setSearch] = useState("");
  let t1;
  if ($[0] !== initialPageId || $[1] !== workflow.pages || $[2] !== workflow.rootPageId) {
    t1 = () => {
      if (initialPageId && initialPageId !== workflow.rootPageId && Object.hasOwn(workflow.pages, initialPageId)) {
        return [workflow.rootPageId, initialPageId];
      }
      return [workflow.rootPageId];
    };
    $[0] = initialPageId;
    $[1] = workflow.pages;
    $[2] = workflow.rootPageId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [pageIds, setPageIds] = useState(t1);
  const [dynamicPages, setDynamicPages] = useState(_temp);
  const resetSearchOnPageChange = workflow.options?.resetSearchOnPageChange !== false;
  const staticPages = workflow.pages;
  const validPageIds = [];
  for (const id of pageIds) {
    if (staticPages[id] || dynamicPages.has(id)) {
      validPageIds.push(id);
    } else {
      break;
    }
  }
  const resolvedPageIds = validPageIds.length > 0 && validPageIds[0] === workflow.rootPageId ? validPageIds : [workflow.rootPageId];
  const needsPageIdsSync = resolvedPageIds.length !== pageIds.length || resolvedPageIds.some((id_0, i) => id_0 !== pageIds[i]);
  if (needsPageIdsSync) {
    setPageIds(resolvedPageIds);
  }
  const oldCurrentPageId = pageIds[pageIds.length - 1];
  const newCurrentPageId = resolvedPageIds[resolvedPageIds.length - 1];
  if (needsPageIdsSync && newCurrentPageId !== oldCurrentPageId && resetSearchOnPageChange && search !== "") {
    setSearch("");
  }
  const resolvedStack = resolvedPageIds.map(id_1 => ({
    page: staticPages[id_1] ?? dynamicPages.get(id_1),
    pageId: id_1
  }));
  const currentPageId = resolvedStack[resolvedStack.length - 1]?.pageId ?? workflow.rootPageId;
  const currentPage = resolvedStack[resolvedStack.length - 1]?.page ?? workflow.pages[workflow.rootPageId];
  const searchRef = useRef(search);
  let t2;
  let t3;
  if ($[4] !== search) {
    t2 = () => {
      searchRef.current = search;
    };
    t3 = [search];
    $[4] = search;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== resetSearchOnPageChange || $[8] !== staticPages) {
    t4 = (page, pageId) => {
      if (!staticPages[pageId]) {
        setDynamicPages(prev => {
          const next = new Map(prev);
          next.set(pageId, page);
          return next;
        });
      }
      setPageIds(prev_0 => [...prev_0, pageId]);
      if (resetSearchOnPageChange) {
        setSearch("");
      }
    };
    $[7] = resetSearchOnPageChange;
    $[8] = staticPages;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const pushPage = t4;
  let t5;
  if ($[10] !== resetSearchOnPageChange) {
    t5 = () => {
      setPageIds(_temp2);
      if (resetSearchOnPageChange) {
        setSearch("");
      }
    };
    $[10] = resetSearchOnPageChange;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const goBack = t5;
  const goBackRef = useRef(goBack);
  let t6;
  let t7;
  if ($[12] !== goBack) {
    t6 = () => {
      goBackRef.current = goBack;
    };
    t7 = [goBack];
    $[12] = goBack;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== resolvedPageIds) {
    t8 = [resolvedPageIds.length];
    $[15] = resolvedPageIds;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  useEffect(() => {
    const handler = e => {
      const target = e.target;
      if (!(target instanceof Element)) {
        return;
      }
      const inNestedOverlay = target.closest("[data-radix-popper-content-wrapper]") !== null || target.closest("[data-radix-select-viewport]") !== null || target.closest("[role=\"alertdialog\"]") !== null;
      const inCommandDialog = target.closest("[data-slot=\"command\"]") !== null;
      const inOtherDialog = !inCommandDialog && target.closest("[role=\"dialog\"]") !== null;
      if (inNestedOverlay || inOtherDialog) {
        return;
      }
      const inCommandInput = target.closest("[data-slot=\"command-input\"]") !== null;
      if (e.key === "Escape" && resolvedPageIds.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        goBackRef.current();
      }
      if (e.key === "Backspace" && resolvedPageIds.length > 1 && searchRef.current === "") {
        if (!inCommandInput) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        goBackRef.current();
      }
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, t8);
  let t9;
  if ($[17] !== pushPage || $[18] !== workflow.pages) {
    t9 = pageId_0 => {
      const page_0 = workflow.pages[pageId_0];
      if (!page_0) {
        return;
      }
      pushPage(page_0, pageId_0);
    };
    $[17] = pushPage;
    $[18] = workflow.pages;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const goTo = t9;
  let t10;
  if ($[20] !== pageIds.length || $[21] !== pushPage) {
    t10 = page_1 => {
      const pageId_1 = page_1.id ?? `page-${pageIds.length}`;
      pushPage(page_1, pageId_1);
    };
    $[20] = pageIds.length;
    $[21] = pushPage;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const goToPage = t10;
  let t11;
  if ($[23] !== resetSearchOnPageChange || $[24] !== workflow.rootPageId) {
    t11 = () => {
      setPageIds([workflow.rootPageId]);
      if (resetSearchOnPageChange) {
        setSearch("");
      }
    };
    $[23] = resetSearchOnPageChange;
    $[24] = workflow.rootPageId;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const resetWorkflow = t11;
  const context = {
    close,
    currentPage,
    currentPageId,
    goBack,
    goTo,
    goToPage,
    resetWorkflow,
    search,
    setSearch
  };
  const handleSelectNode = node => {
    bb162: switch (node.kind) {
      case "action":
        {
          if (node.closeOnSelect !== false) {
            close();
          }
          Promise.resolve(node.onSelect(context)).catch(_temp3);
          break bb162;
        }
      case "dynamic-page":
        {
          const page_2 = node.createPage(context);
          const pageId_2 = page_2.id ?? node.id ?? `page-${pageIds.length}`;
          pushPage(page_2, pageId_2);
          break bb162;
        }
      case "link":
        {
          if (node.closeOnSelect !== false) {
            close();
          }
          push(node.href);
          break bb162;
        }
      case "page":
        {
          goTo(node.pageId);
          break bb162;
        }
      case "render":
    }
  };
  const showBackItem = resolvedPageIds.length > 1;
  const backItemLabel = workflow.options?.backItem?.label ?? "Back";
  const effectiveInputPlaceholder = currentPage.inputPlaceholder ?? inputPlaceholder ?? "Type a command or search...";
  const T0 = CommandList;
  const t12 = "max-h-[min(28rem,60vh)]";
  const t13 = "CommandList";
  const t14 = "command-workflow-content.tsx";
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="command-workflow-content.tsx">No results found.</CommandEmpty>;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = currentPage.groups.map(group => <CommandGroup heading={group.heading} key={group.id}>{group.items.map(node_0 => {
      if (node_0.kind === "render") {
        return <Fragment key={node_0.id}>{node_0.render(context)}</Fragment>;
      }
      return <CommandItem data-testid={node_0.testId} disabled={node_0.disabled} key={node_0.id} keywords={node_0.keywords} onSelect={() => handleSelectNode(node_0)} value={node_0.value ?? node_0.id}>{node_0.label}{node_0.shortcut && <WorkflowShortcut shortcut={node_0.shortcut} />}</CommandItem>;
    })}</CommandGroup>);
  let t17;
  if ($[27] !== T0 || $[28] !== t15 || $[29] !== t16) {
    t17 = <T0 className={t12} data-sentry-element={t13} data-sentry-source-file={t14}>{t15}{t16}</T0>;
    $[27] = T0;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== showBackItem) {
    t18 = <CommandWorkflowFooter canGoBack={showBackItem} data-sentry-element="CommandWorkflowFooter" data-sentry-source-file="command-workflow-content.tsx" />;
    $[31] = showBackItem;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  return <><CommandInput data-testid={inputTestId} onValueChange={setSearch} placeholder={effectiveInputPlaceholder} value={search} data-sentry-element="CommandInput" data-sentry-source-file="command-workflow-content.tsx" />{currentPage.title && <h2 className="sr-only">{currentPage.title}</h2>}{showBackItem && <div className="flex h-9 items-center gap-1 border-b px-2 text-xs text-muted-foreground"><button aria-label="Go back to commands" className="flex h-7 items-center gap-1 rounded-md px-1.5 outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring" onClick={goBack} type="button"><ArrowLeft className="size-3.5 shrink-0" />{backItemLabel}</button>{currentPage.title && <><span aria-hidden="true">/</span><span className="truncate font-medium text-foreground">{currentPage.title}</span></>}</div>}{t17}{t18}</>;
}
function _temp3(error) {
  console.error("Command action failed", error);
}
function _temp2(prev_1) {
  if (prev_1.length <= 1) {
    return prev_1;
  }
  return prev_1.slice(0, -1);
}
function _temp() {
  return new Map();
}
function CommandWorkflowFooter(t0) {
  const $ = _c(5);
  const {
    canGoBack
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="flex items-center gap-1"><KbdGroup data-sentry-element="KbdGroup" data-sentry-source-file="command-workflow-content.tsx"><Kbd data-sentry-element="Kbd" data-sentry-source-file="command-workflow-content.tsx">↑</Kbd><Kbd data-sentry-element="Kbd" data-sentry-source-file="command-workflow-content.tsx">↓</Kbd></KbdGroup>Navigate</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="flex items-center gap-3">{t1}<span className="flex items-center gap-1"><Kbd data-sentry-element="Kbd" data-sentry-source-file="command-workflow-content.tsx">↵</Kbd>Open</span></span>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Kbd data-sentry-element="Kbd" data-sentry-source-file="command-workflow-content.tsx">Esc</Kbd>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = canGoBack ? "Back" : "Close";
  let t5;
  if ($[3] !== t4) {
    t5 = <div aria-label="Command palette keyboard controls" className="flex items-center justify-between gap-3 border-t px-3 py-2 text-[11px] text-muted-foreground" data-sentry-component="CommandWorkflowFooter" data-sentry-source-file="command-workflow-content.tsx">{t2}<span className="flex items-center gap-1">{t3}{t4}</span></div>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  return t5;
}
function WorkflowShortcut(t0) {
  const $ = _c(12);
  const {
    shortcut
  } = t0;
  if (typeof shortcut === "string") {
    let T0;
    let T1;
    let t1;
    if ($[0] !== shortcut) {
      const keys = shortcut.split(" ");
      T1 = CommandShortcut;
      T0 = KbdGroup;
      t1 = keys.map(_temp4);
      $[0] = shortcut;
      $[1] = T0;
      $[2] = T1;
      $[3] = t1;
    } else {
      T0 = $[1];
      T1 = $[2];
      t1 = $[3];
    }
    let t2;
    if ($[4] !== T0 || $[5] !== t1) {
      t2 = <T0>{t1}</T0>;
      $[4] = T0;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== T1 || $[8] !== t2) {
      t3 = <T1>{t2}</T1>;
      $[7] = T1;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  let t1;
  if ($[10] !== shortcut) {
    t1 = <CommandShortcut data-sentry-element="CommandShortcut" data-sentry-component="WorkflowShortcut" data-sentry-source-file="command-workflow-content.tsx">{shortcut}</CommandShortcut>;
    $[10] = shortcut;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
}
function _temp4(key, i) {
  return <Kbd key={key + i}>{key}</Kbd>;
}